import * as THREE from 'three';

import { buildFeatureOverlay } from './digital-twin-geometry.js';
import type { OverlayTerrainContext } from './digital-twin-overlay.js';
import type { PreparedFeature, VectorTileCoordinates } from './feature-preparation.js';
import { OverlayGeometryBatch } from './overlay-geometry-batch.js';
import {
  clipVectorTileFeature,
  parseVectorTileKey,
  vectorTileRegions,
} from './vector-tile-clipping.js';
import type { FeatureOverlay } from '../../types/terrain-types.js';
import type { VectorTileLayerConfig } from '../../types/vector-tile-layer.js';

interface VectorTileGroupEntry {
  batch: OverlayGeometryBatch;
  overlays: FeatureOverlay[];
  controller: AbortController;
  features: PreparedFeature[];
  tile: VectorTileCoordinates;
  coverage?: string;
}

/** Shared vector tiles render only their unpadded area not covered by finer tiles. */
export class VectorTileOverlay {
  readonly group = new THREE.Group();
  private readonly vectorTiles = new Map<string, VectorTileGroupEntry>();
  private readonly refCounts = new Map<string, number>();
  private readonly terrainTileRefs = new Map<string, Set<string>>();
  private visible = false;
  private lineWidth = 3;
  private readonly lineResolution = new THREE.Vector2(1, 1);
  private readonly color: THREE.Color;

  constructor(
    private readonly terrain: OverlayTerrainContext,
    layerId: string,
    color: VectorTileLayerConfig['color'],
  ) {
    this.color = new THREE.Color().setRGB(
      color[0] / 255,
      color[1] / 255,
      color[2] / 255,
      THREE.SRGBColorSpace,
    );
    this.group.name = `VectorTileLayer:${layerId}`;
    this.group.visible = false;
  }

  hasTile(tileKey: string): boolean {
    return this.terrainTileRefs.has(tileKey);
  }

  setTileFeatures(tileKey: string, featuresByVectorTile: Map<string, PreparedFeature[]>): void {
    const previousKeys = this.terrainTileRefs.get(tileKey) ?? new Set<string>();
    const origin = this.terrain.requireOrigin();
    this.group.position.copy(origin);
    const usedKeys = new Set<string>();
    let coverageChanged = false;
    for (const [vectorTileKey, features] of featuresByVectorTile) {
      usedKeys.add(vectorTileKey);
      if (previousKeys.has(vectorTileKey)) continue;
      this.refCounts.set(vectorTileKey, (this.refCounts.get(vectorTileKey) ?? 0) + 1);
      if (this.vectorTiles.has(vectorTileKey)) continue;

      const batch = new OverlayGeometryBatch(
        { width: this.lineWidth, resolution: this.getRendererResolution() },
        this.color,
        this.terrain.workQueue,
      );
      const entry: VectorTileGroupEntry = {
        batch,
        overlays: [],
        controller: new AbortController(),
        features,
        tile: parseVectorTileKey(vectorTileKey),
      };
      this.vectorTiles.set(vectorTileKey, entry);
      this.group.add(batch.group);
      coverageChanged = true;
    }
    for (const key of previousKeys) {
      if (!usedKeys.has(key)) coverageChanged = this.releaseVectorTile(key) || coverageChanged;
    }
    if (usedKeys.size) this.terrainTileRefs.set(tileKey, usedKeys);
    else this.terrainTileRefs.delete(tileKey);
    if (coverageChanged) this.refreshCoverage();
  }

  private refreshCoverage(): void {
    const descendants = new Map<string, VectorTileCoordinates[]>();
    for (const { tile } of this.vectorTiles.values()) {
      for (let z = tile.z - 1; z >= 0; z--) {
        const scale = 2 ** (tile.z - z);
        const key = `${z}/${Math.floor(tile.x / scale)}/${Math.floor(tile.y / scale)}`;
        if (!this.vectorTiles.has(key)) continue;
        const children = descendants.get(key) ?? [];
        children.push(tile);
        descendants.set(key, children);
        // A resident child already masks all of its descendants from this ancestor.
        break;
      }
    }
    for (const [vectorTileKey, entry] of this.vectorTiles) {
      const regions = vectorTileRegions(entry.tile, descendants.get(vectorTileKey) ?? []);
      const coverage = JSON.stringify(regions);
      if (coverage === entry.coverage) continue;
      if (entry.coverage !== undefined) {
        this.disposeEntry(entry);
        entry.batch = new OverlayGeometryBatch(
          { width: this.lineWidth, resolution: this.getRendererResolution() },
          this.color,
          this.terrain.workQueue,
        );
        entry.overlays = [];
        entry.controller = new AbortController();
        this.group.add(entry.batch.group);
      }
      entry.coverage = coverage;
      if (!regions.length) continue;
      const batch = entry.batch;
      const install = function* (owner: VectorTileOverlay): Generator<void> {
        for (const feature of entry.features) {
          const clipped = clipVectorTileFeature(feature, regions);
          if (!clipped) {
            yield;
            continue;
          }
          const overlay = yield* buildFeatureOverlay(clipped, batch);
          entry.overlays.push(overlay);
          owner.terrain.draper.register(
            overlay,
            () => owner.group.visible && overlay.group.visible,
          );
          yield;
        }
      };
      void this.terrain.workQueue
        .run(install(this), entry.controller.signal, () => this.visible)
        .catch(error => {
          if (!(error instanceof Error && error.name === 'AbortError')) {
            console.error(`Failed to install vector tile ${vectorTileKey}:`, error);
          }
        });
    }
  }

  removeTile(tileKey: string): void {
    const vectorTileKeys = this.terrainTileRefs.get(tileKey);
    if (!vectorTileKeys) return;
    let coverageChanged = false;
    for (const vectorTileKey of vectorTileKeys) {
      coverageChanged = this.releaseVectorTile(vectorTileKey) || coverageChanged;
    }
    this.terrainTileRefs.delete(tileKey);
    if (coverageChanged) this.refreshCoverage();
  }

  private releaseVectorTile(key: string): boolean {
    const refCount = (this.refCounts.get(key) ?? 1) - 1;
    if (refCount > 0) {
      this.refCounts.set(key, refCount);
      return false;
    }
    this.refCounts.delete(key);
    const entry = this.vectorTiles.get(key);
    if (!entry) return false;
    this.disposeEntry(entry);
    this.vectorTiles.delete(key);
    return true;
  }

  private disposeEntry(entry: VectorTileGroupEntry): void {
    entry.controller.abort();
    for (const overlay of entry.overlays) this.terrain.draper.unregister(overlay);
    this.group.remove(entry.batch.group);
    entry.batch.dispose();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.group.visible = visible;
  }

  isVisible(): boolean {
    return this.visible;
  }

  setLineWidth(width: number): void {
    this.lineWidth = width;
    for (const entry of this.vectorTiles.values()) entry.batch.setLineWidth(width);
  }

  update(): void {
    if (!this.visible || !this.terrain.hasOrigin()) return;
    const resolution = this.getRendererResolution();
    for (const entry of this.vectorTiles.values()) entry.batch.setResolution(resolution);
  }

  flush(camera?: THREE.Camera): void {
    if (!this.visible) return;
    for (const entry of this.vectorTiles.values()) entry.batch.flush(camera);
  }

  dispose(): void {
    for (const entry of this.vectorTiles.values()) this.disposeEntry(entry);
    this.vectorTiles.clear();
    this.refCounts.clear();
    this.terrainTileRefs.clear();
    this.lineResolution.set(1, 1);
  }

  private getRendererResolution(): THREE.Vector2 {
    const canvas = this.terrain.rendererCanvas();
    if (canvas) this.lineResolution.set(canvas.clientWidth || 1, canvas.clientHeight || 1);
    return this.lineResolution;
  }
}
